import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@pilot-hub/ui'
import { api, checklistenApi } from '@pilot-hub/api-client'
import { Power } from 'lucide-react'
import { useAufforderungen, aufforderungenNeuLaden, type VorflugAuff } from '../hooks/useAufforderungen'
import { ChecklistZeile } from './ChecklistZeile'
import { useAlleOk } from './AlleOk'
import { ChecklistenEingabe } from './ChecklistenEingabe'
import { istVerbindungsfehler } from '../utils/flugQueue'

// Pop-up "Vorflugkontrolle" im Pilot-Modus, hängt im Layout (also auf jeder Seite): öffnet sich von selbst, sobald eine
// eingeschaltete Drohne eine Vorflug-Aufforderung für den Piloten hat (siehe CloudAufforderungen / pilot-api).
//  - je Einschalten (Drohne + eingeschaltet_am) höchstens einmal automatisch; wiederholtes "Gerät online" ändert daran nichts
//  - Neuladen der Seite öffnet es erneut, solange die Aufforderung offen ist und nicht weggeklickt wurde
//  - "Später" = weggeklickt (bleibt in localStorage); das Banner mit Knopf bleibt als Rückweg
//  - übernimmt jemand anderes, schließt es sich mit Hinweis
//  - "Abschließen" -> Flugerfassung legt den vorbereiteten Flug an (Zusammenführung beim Abheben wie bei "Flug starten")

export const POPUP_WEG_KEY = 'pilot-hub-vorflug-popup-weg'
const vorlageKey = (einsatzId: number) => `pilot-hub-vorflug-vorlage-${einsatzId}`   // gleicher Zwischenspeicher wie die Flugerfassung
export const popupKey = (id: number, seit: string | null) => `${id}@${seit ?? ''}`

export function popupWegLesen(): string[] {
  try { return JSON.parse(localStorage.getItem(POPUP_WEG_KEY) || '[]') } catch { return [] }
}
export function popupWegMerken(k: string) {
  try {
    const liste = popupWegLesen()
    if (!liste.includes(k)) localStorage.setItem(POPUP_WEG_KEY, JSON.stringify([...liste, k].slice(-50)))
  } catch { /* egal */ }
}

interface Punkt { vorlage_item_id: number; text: string; typ: 'CHECK' | 'TEXT' | 'NUMBER' | 'PHOTO'; pflicht: boolean; sortierung: number; hinweis: string | null; wert: string | null }

async function vorlageLaden(einsatzId: number): Promise<Punkt[]> {
  try {
    const r = await checklistenApi.getVorflugVorlage(einsatzId)
    const daten = r.data as { items: Punkt[] }
    try { localStorage.setItem(vorlageKey(einsatzId), JSON.stringify(daten)) } catch { /* egal */ }
    return daten.items ?? []
  } catch (e: any) {
    if (e?.response?.status === 404) return []
    if (!istVerbindungsfehler(e)) throw e
    try { return (JSON.parse(localStorage.getItem(vorlageKey(einsatzId)) || 'null') as { items: Punkt[] } | null)?.items ?? [] } catch { return [] }
  }
}

export function VorflugPopup({ pilotMode }: { pilotMode: 'PILOT' | 'CO_PILOT' }) {
  const { daten, geladen } = useAufforderungen()
  const { frage: frageAlleOk, dialog: alleOkDialog } = useAlleOk()
  const navigate = useNavigate()
  const [aktiv, setAktiv] = useState<VorflugAuff | null>(null)
  const [punkte, setPunkte] = useState<Punkt[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [uebernommen, setUebernommen] = useState(false)
  const geoeffnet = useRef<Set<string>>(new Set())   // in dieser Seitensitzung schon automatisch geöffnet (Neuladen leert es)
  const laedt = useRef(false)

  const istPilot = pilotMode === 'PILOT'
  const key = aktiv ? popupKey(aktiv.drohne_id, aktiv.eingeschaltet_am) : null
  const aktuell = aktiv ? daten.vorflug.find(v => v.drohne_id === aktiv.drohne_id && v.eingeschaltet_am === aktiv.eingeschaltet_am) : null

  function hinweisZeigen(t: string) {
    setHinweis(t)
    window.setTimeout(() => setHinweis(h => (h === t ? null : h)), 7000)
  }
  function schliessen() { setAktiv(null); setPunkte([]); setFehler(null); setUebernommen(false) }

  // Automatisch öffnen
  useEffect(() => {
    if (!geladen || !istPilot || aktiv || laedt.current) return
    const weg = popupWegLesen()
    const kandidat = daten.vorflug.find(v => (v.status === 'OFFEN' || v.status === 'VON_MIR')
      && !weg.includes(popupKey(v.drohne_id, v.eingeschaltet_am)) && !geoeffnet.current.has(popupKey(v.drohne_id, v.eingeschaltet_am)))
    if (!kandidat) return
    const k = popupKey(kandidat.drohne_id, kandidat.eingeschaltet_am)
    laedt.current = true
    void vorlageLaden(kandidat.einsatz_id).then(items => {
      geoeffnet.current.add(k)   // auch ohne Vorlage: nicht erneut versuchen, das Banner bleibt
      if (!items.length) return
      setPunkte(items.map(i => ({ ...i, wert: null })))
      setUebernommen(kandidat.status === 'VON_MIR')
      setAktiv(kandidat)
    }).catch(() => { geoeffnet.current.add(k) }).finally(() => { laedt.current = false })
  }, [geladen, istPilot, aktiv, daten.vorflug])

  // Modus-Wechsel auf Co-Pilot: Pop-up schließen (ohne die Aufforderung wegzuklicken)
  useEffect(() => { if (!istPilot && aktiv) schliessen() }, [istPilot]) // eslint-disable-line react-hooks/exhaustive-deps

  // Übernimmt ein anderer Pilot (oder ist der Flug schon vorbereitet), schließt sich das Pop-up mit Hinweis.
  // Fehlt die Aufforderung nur kurz (Telemetrie-Lücke), bleibt es offen und nichts geht verloren.
  const da = !!aktuell
  useEffect(() => {
    if (!aktiv || !geladen) return
    if (!aktuell) {
      const t = window.setTimeout(() => { hinweisZeigen('Die Vorflugkontrolle ist nicht mehr offen.'); schliessen() }, 25000)
      return () => window.clearTimeout(t)
    }
    if (aktuell.status === 'IN_ARBEIT' || aktuell.status === 'UEBERNOMMEN') {
      hinweisZeigen(`${aktuell.von ?? 'Ein anderer Pilot'} hat die Vorflugkontrolle für ${aktuell.drohne} übernommen.`)
      schliessen()
    }
  }, [aktiv, aktuell?.status, aktuell?.von, da, geladen]) // eslint-disable-line react-hooks/exhaustive-deps

  async function uebernehmen(): Promise<boolean> {
    if (!aktiv) return false
    if (uebernommen) return true
    try {
      await api.post(`/pilot/vorflug/${aktiv.drohne_id}/uebernehmen`)
    } catch (err: any) {
      if (!istVerbindungsfehler(err)) {
        hinweisZeigen(err?.response?.data?.detail || 'Vorflugkontrolle konnte nicht übernommen werden')
        if (key) popupWegMerken(key)
        schliessen(); aufforderungenNeuLaden()
        return false
      }
    }
    setUebernommen(true)
    return true
  }

  async function setzen(id: number, wert: string) {
    if (!(await uebernehmen())) return
    setPunkte(p => p.map(x => x.vorlage_item_id === id ? { ...x, wert } : x))
  }
  async function alleSetzen() {
    if (!(await frageAlleOk(punkte.filter(p => p.pflicht && p.typ === 'CHECK' && !p.wert).length))) return
    if (!(await uebernehmen())) return
    setPunkte(p => p.map(x => x.pflicht && x.typ === 'CHECK' && !x.wert ? { ...x, wert: 'true' } : x))
  }
  function spaeter() {
    if (aktiv && key) {
      popupWegMerken(key)
      if (uebernommen) void api.post(`/pilot/vorflug/${aktiv.drohne_id}/freigeben`).catch(() => {})
    }
    schliessen(); aufforderungenNeuLaden()
  }
  function abschliessen() {
    if (!aktiv || !key) return
    const offen = punkte.filter(p => p.pflicht && !p.wert).length
    if (offen > 0) { setFehler(`Noch ${offen} Pflichtpunkte offen.`); return }
    const werte = Object.fromEntries(punkte.filter(p => p.wert).map(p => [String(p.vorlage_item_id), p.wert as string]))
    popupWegMerken(key)
    const a = aktiv
    schliessen()
    navigate('/flug-erfassen', { state: { auftrag: { typ: 'vorflug', drohneId: a.drohne_id, einsatzId: a.einsatz_id, werte } } })
  }

  const offen = punkte.filter(p => p.pflicht && !p.wert).length
  return (
    <>
      {alleOkDialog}
      {hinweis && (
        <div className="fixed inset-x-0 bottom-20 z-[1100] flex justify-center px-4 lg:bottom-6" role="status" data-testid="vorflug-popup-hinweis">
          <div className="rounded-xl border border-sky-500/40 bg-dark-bg-secondary px-4 py-3 text-sm text-dark-text shadow-lg">{hinweis}</div>
        </div>
      )}
      {aktiv && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-2 sm:p-4" role="dialog" aria-modal="true" data-testid="vorflug-popup">
          <div className="flex max-h-[94dvh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-sky-500/40 bg-dark-bg">
            <div className="flex items-center gap-3 border-b border-dark-border bg-dark-bg-secondary p-4">
              <Power className="h-6 w-6 shrink-0 text-sky-300" />
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-dark-text">Drohne {aktiv.drohne} eingeschaltet – Vorflugkontrolle</h2>
                <p className="truncate text-sm text-dark-text-muted">
                  {aktiv.mehrere_empfaenger ? 'Wer zuerst startet, übernimmt · ' : ''}{aktiv.einsatz_titel}
                </p>
              </div>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto p-3 sm:p-4">
              {fehler && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{fehler}</div>}
              {punkte.map(item => (
                <ChecklistZeile key={item.vorlage_item_id} typ={item.typ} className="rounded-xl border border-dark-border bg-dark-bg-tertiary/50 p-3">
                  <div className="flex items-center gap-3">
                    {item.typ === 'CHECK' && (
                      <input type="checkbox" checked={item.wert === 'true'} onChange={e => void setzen(item.vorlage_item_id, e.target.checked ? 'true' : '')}
                        className="haken" />
                    )}
                    {item.typ === 'TEXT' && <ChecklistenEingabe type="text" wert={item.wert || ''} onSpeichern={v => void setzen(item.vorlage_item_id, v)}
                      className="min-h-[48px] flex-1 rounded border border-dark-border bg-dark-bg px-3 py-2 text-base text-dark-text" placeholder="Freitext..." />}
                    {item.typ === 'NUMBER' && <ChecklistenEingabe type="number" wert={item.wert && Number.isFinite(Number(item.wert)) ? item.wert : ''} onSpeichern={v => void setzen(item.vorlage_item_id, v)}
                      className="min-h-[48px] w-32 rounded border border-dark-border bg-dark-bg px-3 py-2 text-base text-dark-text" placeholder="Zahl" />}
                    <div className="min-w-0 flex-1">
                      <p className={`text-base font-medium ${item.pflicht ? 'text-dark-text' : 'text-dark-text-muted'}`}>
                        {item.text}{item.pflicht && <span className="checklist-pflicht">Pflicht</span>}
                      </p>
                      {item.hinweis && <p className="mt-0.5 text-sm text-dark-text-muted">{item.hinweis}</p>}
                    </div>
                  </div>
                </ChecklistZeile>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-dark-border bg-dark-bg-secondary p-3 sm:p-4">
              <Button variant="secondary" className="!min-h-[48px]" onClick={spaeter} data-testid="vorflug-popup-spaeter">Später</Button>
              <Button variant="secondary" className="!min-h-[48px]" onClick={() => void alleSetzen()}>Alle Pflichtpunkte ok</Button>
              <Button className="!min-h-[48px]" onClick={abschliessen} disabled={offen > 0} data-testid="vorflug-popup-abschliessen">
                {offen > 0 ? `Noch ${offen} offen` : 'Abschließen'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
